/**
 * TemplatrManager.ts - Universal Template Management System
 * 
 * CHANGE HISTORY:
 * - 251102_0916 [pending] danielbeer: Ground-up rebuild with full orchestrator integration
 * 
 * Universal template system supporting:
 * - Document templates (emails, reports, contracts)
 * - Section templates (headers, footers, signatures)
 * - Pattern templates (search, filters, queries)
 * - Data templates (mappings, structures)
 * - System, user, and team scopes
 * - Full integration with Organizr (categories) and Listr (dynamic lists)
 */

import type {
  Template,
  TemplateType,
  TemplateScope,
  TemplateContext,
  TemplateVariable,
  CreateTemplateOptions,
  UpdateTemplateOptions,
  ApplyTemplateOptions,
  ApplyTemplateResult,
  TemplateSearchOptions,
  TemplateExportFormat,
  TemplateImportResult,
  TemplatrOptions,
  TemplateStats,
  CustomTemplateHandler
} from './types';

/**
 * TemplatrManager - Universal Template Management
 * 
 * Architecture:
 * - Uses Actr for all CRUD operations
 * - Uses Aggregatr for template storage
 * - Uses Organizr for static categories
 * - Uses Listr for dynamic lists (recent, popular, by-context)
 * - Uses Filtr for advanced filtering
 * - Uses Translatr for format conversion
 */
export class TemplatrManager {
  private actr: any;
  private aggregatr: any;
  private organizr: any;
  private listr: any;
  private distillr: any;
  private translatr: any;
  private workspaceId?: string;
  private userId?: string;
  
  // Local cache
  private templateCache: Map<string, Template> = new Map();
  
  // Custom handlers
  private customHandlers: Map<string, CustomTemplateHandler> = new Map();
  
  // Singleton instance
  private static instance: TemplatrManager | null = null;

  constructor(options: TemplatrOptions = {}) {
    this.actr = options.actr;
    this.aggregatr = options.aggregatr;
    this.organizr = options.organizr;
    this.listr = options.listr;
    this.filtr = options.filtr;
    this.translatr = options.translatr;
    this.workspaceId = options.workspaceId;
    this.userId = options.userId;

    // Register actions with Actr
    if (this.actr) {
      this._registerActions();
    }

    // Initialize categories in Organizr
    if (this.organizr) {
      this._initializeCategories();
    }

    // Initialize dynamic lists in Listr
    if (this.listr) {
      this._initializeLists();
    }
  }

  /**
   * Register Actr actions
   */
  private _registerActions(): void {
    this.actr.registerAction('templatr:create', this._createTemplateAction.bind(this));
    this.actr.registerAction('templatr:read', this._readTemplateAction.bind(this));
    this.actr.registerAction('templatr:update', this._updateTemplateAction.bind(this));
    this.actr.registerAction('templatr:delete', this._deleteTemplateAction.bind(this));
    this.actr.registerAction('templatr:apply', this._applyTemplateAction.bind(this));
    this.actr.registerAction('templatr:search', this._searchTemplatesAction.bind(this));
  }

  /**
   * Initialize Organizr categories
   */
  private async _initializeCategories(): Promise<void> {
    // Create default template categories
    const categories = [
      { id: 'documents', name: 'Documents', icon: '📄' },
      { id: 'emails', name: 'Emails', icon: '📧' },
      { id: 'reports', name: 'Reports', icon: '📊' },
      { id: 'contracts', name: 'Contracts', icon: '📝' },
      { id: 'patterns', name: 'Patterns', icon: '🔍' },
      { id: 'mappings', name: 'Mappings', icon: '🔗' },
      { id: 'workflows', name: 'Workflows', icon: '⚙️' },
      { id: 'favorites', name: 'Favorites', icon: '⭐' }
    ];

    for (const category of categories) {
      await this.organizr.createCategory(category);
    }
  }

  /**
   * Initialize Listr dynamic lists
   */
  private async _initializeLists(): Promise<void> {
    // Recent templates
    await this.listr.createList({
      name: 'Recent Templates',
      source: 'aggregatr',
      query: {
        collection: 'templates',
        filters: {},
        sortBy: 'metadata.lastUsed',
        limit: 10
      },
      displayConfig: {
        displayField: 'name',
        valueField: 'id',
        additionalFields: ['type', 'context', 'metadata.lastUsed'],
        formatters: {
          'metadata.lastUsed': (date: Date) => new Date(date).toLocaleDateString()
        }
      },
      refresh: 'on-access',
      ttl: 300000
    });

    // Popular templates
    await this.listr.createList({
      name: 'Popular Templates',
      source: 'aggregatr',
      query: {
        collection: 'templates',
        filters: {},
        sortBy: 'metadata.usageCount',
        limit: 10
      },
      displayConfig: {
        displayField: 'name',
        valueField: 'id',
        additionalFields: ['type', 'metadata.usageCount']
      },
      refresh: 'on-access',
      ttl: 600000
    });

    // Favorite templates
    await this.listr.createList({
      name: 'Favorite Templates',
      source: 'aggregatr',
      query: {
        collection: 'templates',
        filters: {
          'metadata.favorite': true
        }
      },
      displayConfig: {
        displayField: 'name',
        valueField: 'id',
        additionalFields: ['type', 'context']
      },
      refresh: 'on-change',
      ttl: 300000
    });
  }

  /**
   * Create a new template
   */
  async createTemplate(options: CreateTemplateOptions): Promise<Template> {
    return await this.actr.execute('templatr:create', options);
  }

  private async _createTemplateAction(options: CreateTemplateOptions): Promise<Template> {
    const template: Template = {
      id: `template-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      name: options.name,
      description: options.description,
      type: options.type,
      scope: options.scope || 'user',
      context: options.context,
      content: options.content,
      variables: options.variables,
      config: options.config,
      metadata: {
        created: new Date(),
        updated: new Date(),
        createdBy: this.userId || 'system',
        updatedBy: this.userId || 'system',
        version: '1.0.0',
        usageCount: 0,
        tags: options.tags || [],
        category: options.category,
        favorite: false
      }
    };

    // Validate template
    const errors = await this._validateTemplate(template);
    if (errors.length > 0) {
      throw new Error(`Template validation failed: ${errors.join(', ')}`);
    }

    // Save to Aggregatr
    if (this.aggregatr && this.workspaceId) {
      await this.aggregatr.saveToDataset(this.workspaceId, 'templates', template);
    }

    // Cache
    this.templateCache.set(template.id, template);

    return template;
  }

  /**
   * Get template by ID
   */
  async getTemplate(id: string): Promise<Template | null> {
    return await this.actr.execute('templatr:read', { id });
  }

  private async _readTemplateAction({ id }: { id: string }): Promise<Template | null> {
    // Check cache
    if (this.templateCache.has(id)) {
      return this.templateCache.get(id)!;
    }

    // Load from Aggregatr
    if (this.aggregatr && this.workspaceId) {
      const template = await this.aggregatr.getFromDataset(
        this.workspaceId,
        'templates',
        id
      );
      
      if (template) {
        this.templateCache.set(id, template);
        return template;
      }
    }

    return null;
  }

  /**
   * Update template
   */
  async updateTemplate(id: string, updates: UpdateTemplateOptions): Promise<Template> {
    return await this.actr.execute('templatr:update', { id, updates });
  }

  private async _updateTemplateAction({
    id,
    updates
  }: {
    id: string;
    updates: UpdateTemplateOptions;
  }): Promise<Template> {
    const template = await this.getTemplate(id);
    if (!template) {
      throw new Error(`Template not found: ${id}`);
    }

    // Check permissions (system templates are read-only)
    if (template.scope === 'system') {
      throw new Error('Cannot modify system templates');
    }

    // Apply updates
    if (updates.name) template.name = updates.name;
    if (updates.description !== undefined) template.description = updates.description;
    if (updates.context) template.context = updates.context;
    if (updates.content) template.content = updates.content;
    if (updates.variables) template.variables = updates.variables;
    if (updates.config) template.config = { ...template.config, ...updates.config };
    if (updates.tags) template.metadata.tags = updates.tags;
    if (updates.category !== undefined) template.metadata.category = updates.category;

    // Update metadata
    template.metadata.updated = new Date();
    template.metadata.updatedBy = this.userId || 'system';

    // Validate
    const errors = await this._validateTemplate(template);
    if (errors.length > 0) {
      throw new Error(`Template validation failed: ${errors.join(', ')}`);
    }

    // Save
    if (this.aggregatr && this.workspaceId) {
      await this.aggregatr.saveToDataset(this.workspaceId, 'templates', template);
    }

    // Update cache
    this.templateCache.set(id, template);

    return template;
  }

  /**
   * Delete template
   */
  async deleteTemplate(id: string): Promise<boolean> {
    return await this.actr.execute('templatr:delete', { id });
  }

  private async _deleteTemplateAction({ id }: { id: string }): Promise<boolean> {
    const template = await this.getTemplate(id);
    if (!template) {
      return false;
    }

    // Check permissions
    if (template.scope === 'system') {
      throw new Error('Cannot delete system templates');
    }

    // Delete from Aggregatr
    if (this.aggregatr && this.workspaceId) {
      await this.aggregatr.deleteFromDataset(this.workspaceId, 'templates', id);
    }

    // Remove from cache
    this.templateCache.delete(id);

    return true;
  }

  /**
   * Apply template with variables
   */
  async applyTemplate(
    id: string,
    options: ApplyTemplateOptions = {}
  ): Promise<ApplyTemplateResult> {
    return await this.actr.execute('templatr:apply', { id, options });
  }

  private async _applyTemplateAction({
    id,
    options
  }: {
    id: string;
    options: ApplyTemplateOptions;
  }): Promise<ApplyTemplateResult> {
    const template = await this.getTemplate(id);
    if (!template) {
      return {
        success: false,
        errors: [`Template not found: ${id}`]
      };
    }

    // Check for custom handler
    const handler = this.customHandlers.get(template.type);
    if (handler) {
      return await handler.apply(template, options);
    }

    // Default application logic
    const errors: string[] = [];
    const warnings: string[] = [];

    // Validate variables
    if (options.validate !== false && template.variables) {
      const validationErrors = this._validateVariables(
        template.variables,
        options.variables || {}
      );
      errors.push(...validationErrors);
    }

    if (errors.length > 0) {
      return { success: false, errors };
    }

    // Replace variables in content
    let content = template.content;
    const variables = options.variables || {};

    // Simple variable replacement: {{variableName}}
    content = content.replace(/\{\{(\w+)\}\}/g, (match, varName) => {
      if (variables[varName] !== undefined) {
        return String(variables[varName]);
      }
      warnings.push(`Variable not provided: ${varName}`);
      return match;
    });

    // Update usage statistics (if not preview)
    if (!options.preview) {
      template.metadata.usageCount++;
      template.metadata.lastUsed = new Date();
      await this.updateTemplate(id, {});
    }

    return {
      success: true,
      content,
      warnings: warnings.length > 0 ? warnings : undefined,
      metadata: {
        templateId: id,
        appliedAt: new Date(),
        variables
      }
    };
  }

  /**
   * Search templates
   */
  async searchTemplates(options: TemplateSearchOptions = {}): Promise<Template[]> {
    return await this.actr.execute('templatr:search', options);
  }

  private async _searchTemplatesAction(
    options: TemplateSearchOptions
  ): Promise<Template[]> {
    if (!this.aggregatr || !this.workspaceId) {
      return [];
    }

    // Build filters
    const filters: Record<string, any> = {};

    if (options.type) {
      filters.type = { $in: options.type };
    }

    if (options.scope) {
      filters.scope = { $in: options.scope };
    }

    if (options.tags && options.tags.length > 0) {
      filters['metadata.tags'] = { $in: options.tags };
    }

    if (options.category) {
      filters['metadata.category'] = options.category;
    }

    if (options.favorites) {
      filters['metadata.favorite'] = true;
    }

    // Get templates from Aggregatr
    let templates = await this.aggregatr.queryDataset(
      this.workspaceId,
      'templates',
      filters
    );

    // Text search
    if (options.query) {
      const query = options.query.toLowerCase();
      templates = templates.filter((t: Template) =>
        t.name.toLowerCase().includes(query) ||
        t.description?.toLowerCase().includes(query) ||
        t.metadata.tags.some(tag => tag.toLowerCase().includes(query))
      );
    }

    // Context filter
    if (options.context && options.context.length > 0) {
      templates = templates.filter((t: Template) =>
        options.context!.some(ctx => t.context.includes(ctx))
      );
    }

    // Sort
    const sortBy = options.sortBy || 'name';
    const sortOrder = options.sortOrder || 'asc';
    templates.sort((a: Template, b: Template) => {
      let aVal, bVal;
      
      switch (sortBy) {
        case 'name':
          aVal = a.name;
          bVal = b.name;
          break;
        case 'created':
          aVal = a.metadata.created;
          bVal = b.metadata.created;
          break;
        case 'updated':
          aVal = a.metadata.updated;
          bVal = b.metadata.updated;
          break;
        case 'usage':
          aVal = a.metadata.usageCount;
          bVal = b.metadata.usageCount;
          break;
        case 'lastUsed':
          aVal = a.metadata.lastUsed || new Date(0);
          bVal = b.metadata.lastUsed || new Date(0);
          break;
        default:
          aVal = a.name;
          bVal = b.name;
      }

      if (sortOrder === 'asc') {
        return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      } else {
        return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
      }
    });

    // Limit
    if (options.limit) {
      templates = templates.slice(0, options.limit);
    }

    return templates;
  }

  /**
   * Get template statistics
   */
  async getStats(): Promise<TemplateStats> {
    const templates = await this.searchTemplates();

    const stats: TemplateStats = {
      total: templates.length,
      byType: {} as Record<TemplateType, number>,
      byScope: {} as Record<TemplateScope, number>,
      byContext: {} as Record<TemplateContext, number>,
      totalUsage: 0,
      mostUsed: [],
      recentlyUsed: [],
      favorites: 0
    };

    templates.forEach(t => {
      // By type
      stats.byType[t.type] = (stats.byType[t.type] || 0) + 1;
      
      // By scope
      stats.byScope[t.scope] = (stats.byScope[t.scope] || 0) + 1;
      
      // By context
      t.context.forEach(ctx => {
        stats.byContext[ctx] = (stats.byContext[ctx] || 0) + 1;
      });
      
      // Usage
      stats.totalUsage += t.metadata.usageCount;
      
      // Favorites
      if (t.metadata.favorite) {
        stats.favorites++;
      }
    });

    // Most used
    stats.mostUsed = [...templates]
      .sort((a, b) => b.metadata.usageCount - a.metadata.usageCount)
      .slice(0, 10);

    // Recently used
    stats.recentlyUsed = [...templates]
      .filter(t => t.metadata.lastUsed)
      .sort((a, b) => 
        (b.metadata.lastUsed?.getTime() || 0) - (a.metadata.lastUsed?.getTime() || 0)
      )
      .slice(0, 10);

    return stats;
  }

  /**
   * Toggle favorite status
   */
  async toggleFavorite(id: string): Promise<Template> {
    const template = await this.getTemplate(id);
    if (!template) {
      throw new Error(`Template not found: ${id}`);
    }

    template.metadata.favorite = !template.metadata.favorite;
    return await this.updateTemplate(id, {});
  }

  /**
   * Register custom template handler
   */
  registerHandler(handler: CustomTemplateHandler): void {
    this.customHandlers.set(handler.type, handler);
  }

  /**
   * Validate template
   */
  private async _validateTemplate(template: Template): Promise<string[]> {
    const errors: string[] = [];

    if (!template.name || template.name.trim().length === 0) {
      errors.push('Template name is required');
    }

    if (!template.content || template.content.trim().length === 0) {
      errors.push('Template content is required');
    }

    if (!template.context || template.context.length === 0) {
      errors.push('At least one context is required');
    }

    // Custom handler validation
    const handler = this.customHandlers.get(template.type);
    if (handler && handler.validate) {
      const customErrors = await handler.validate(template);
      errors.push(...customErrors);
    }

    return errors;
  }

  /**
   * Validate variables
   */
  private _validateVariables(
    definitions: TemplateVariable[],
    values: Record<string, any>
  ): string[] {
    const errors: string[] = [];

    definitions.forEach(def => {
      const value = values[def.name];

      // Required check
      if (def.required && value === undefined) {
        errors.push(`Required variable missing: ${def.name}`);
        return;
      }

      if (value === undefined) return;

      // Type check
      const actualType = Array.isArray(value) ? 'array' : typeof value;
      if (actualType !== def.type) {
        errors.push(
          `Variable ${def.name} has wrong type: expected ${def.type}, got ${actualType}`
        );
      }

      // Validation rules
      if (def.validation) {
        if (def.validation.pattern && typeof value === 'string') {
          const regex = new RegExp(def.validation.pattern);
          if (!regex.test(value)) {
            errors.push(`Variable ${def.name} does not match pattern`);
          }
        }

        if (def.validation.min !== undefined && typeof value === 'number') {
          if (value < def.validation.min) {
            errors.push(`Variable ${def.name} is below minimum: ${def.validation.min}`);
          }
        }

        if (def.validation.max !== undefined && typeof value === 'number') {
          if (value > def.validation.max) {
            errors.push(`Variable ${def.name} exceeds maximum: ${def.validation.max}`);
          }
        }

        if (def.validation.options && !def.validation.options.includes(value)) {
          errors.push(`Variable ${def.name} is not a valid option`);
        }
      }
    });

    return errors;
  }

  /**
   * Get singleton instance
   */
  static getInstance(options?: TemplatrOptions): TemplatrManager {
    if (!TemplatrManager.instance) {
      TemplatrManager.instance = new TemplatrManager(options);
    }
    return TemplatrManager.instance;
  }

  /**
   * Reset singleton (for testing)
   */
  static resetInstance(): void {
    TemplatrManager.instance = null;
  }
}

// Singleton accessor
export function getTemplatr(options?: TemplatrOptions): TemplatrManager {
  return TemplatrManager.getInstance(options);
}

// Reset for testing
export function resetTemplatr(): void {
  TemplatrManager.resetInstance();
}
